import React, { useEffect, useMemo, useRef, useState } from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import FilterSection from "../../../components/FilterOrderSection";
import PaginationTable from "../../../components/Pagination";
import { Button, Divider, Modal, Radio, Steps } from "antd";
import {
	codeFormatter,
	formatMoney,
	priceFormatterByCurrency,
} from "../../../utils/formatters";
import api from "../../../utils/axios";
import { ColDef } from "ag-grid-community";
import { CloseOutlined, PrinterOutlined } from "@ant-design/icons";
import OrderDetailRow from "../../../components/form/OrderDetailRow";
import { toast } from "react-toastify";
import Buttons from "../../../components/form/Buttons";
import { hasAccess } from "../../../lib/MenuValidation";

const getValueFromOption = (value: string) => {
	if (value?.toString().includes("-")) {
		return value.split("-")[0];
	}
	return value;
};

const RedirectedOrders = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	let grid = useRef<AgGridReact | null>(null);
	const [tableData, setTableData] = useState([]);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [openModal, setOpenModal] = useState<boolean>();
	const [openOrderHistory, setOpenOrderHistory] = useState<boolean>();
	const [rowData, setRowData] = useState<any>();
	const [orderHistory, setOrderHistory] = useState<any[]>([]);
	const [orderRedirectedId, setOrderRedirectedId] = useState<any>();
	const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
	const [selectedFormat, setSelectedFormat] = useState("100x60");
	const hasAccessToA4 = hasAccess("A4_TICKETS_ACCESS");
	const hasAccessToHarleyTickets = hasAccess("HARLEY_TICKETS_ACCESS");
	const [filterData, setFilterData] = useState<any>({
		statusIds: [],
	});

	const hexToRgba = (hex: string, alpha: number) => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r},${g},${b},${alpha})`;
	};

	const fetchOrderHistory = async (orderId: string) => {
		try {
			const response = await api.get("Order/short-order-history", {
				params: {
					orderId: orderId,
				},
			});
			const result = response.data.reverse();
			setOrderHistory(result);
		} catch (error) {
			console.log("Error:", error);
		}
	};

	const fetchRedirectedNewOrder = async (orderId: string) => {
		try {
			const response = await api.get("Order/redirected-order-details", {
				params: {
					orderToRedirectId: orderId,
				},
			});
			if (response.status === 200) {
				const result = response.data;
				setRowData(result);
			}
		} catch (error) {
			console.log("Error:", error);
		}
	};

	const fetchTableData = async () => {
		getOrders(
			pageSize,
			currentPage,
			filterData?.countryIds,
			filterData?.cityIds,
			filterData?.statusIds,
			filterData?.displayId,
			filterData?.orderDescription,
			filterData?.fromData,
			filterData?.toDate,
			filterData?.filterType,
			filterData?.sellerIds,
			filterData?.staffIds,
			filterData?.selectedSettled,
			filterData?.orderTypeFilter
		);
	};

	const fetchOrders = async (
		pageSize: number,
		currentPage: number,
		countryIds?: any[],
		cityIds?: any[],
		statusIds?: any[],
		displayId?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		filterType?: any,
		orderTypeFilter?: any,
		sellerIds?: any[],
		staffIds?: any[],
		selectedSettled?: boolean,
		paginationDisabled?: boolean
	) => {
		const displayIdSearchTerm = displayId || null;
		const response = await api.post("Order/redirected-orders-paginated", {
			pageSize,
			currentPage,
			countryIds:
				countryIds?.map((c) => ({
					id: getValueFromOption(c),
				})) || [],
			cityIds: cityIds?.map((c) => ({ id: getValueFromOption(c) })) || [],
			statusIds:
				statusIds?.map((c) => ({
					id: getValueFromOption(c),
				})) || [],
			displayId,
			orderDescription,
			fromDate,
			toDate,
			filterType,
			orderTypeFilter: orderTypeFilter || null,
			sellerIds:
				sellerIds?.map((c) => ({
					id: getValueFromOption(c),
				})) || [],
			staffIds:
				staffIds?.map((c) => ({ id: getValueFromOption(c) })) || [],
			settled: selectedSettled,
			paginationDisabled,
			displayIdSearchTerm,
		});
		return response.data;
	};

	const getOrders = async (
		pageSize: number,
		currentPage: number,
		countryIds?: any[],
		cityIds?: any[],
		statusIds?: any[],
		displayId?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		filterType?: any,
		orderTypeFilter?: any,
		sellerIds?: any[],
		staffIds?: any[],
		selectedSettled?: boolean,
		paginationDisabled?: boolean
	) => {
		try {
			const responseData = await fetchOrders(
				pageSize,
				currentPage,
				countryIds,
				cityIds,
				statusIds,
				displayId,
				orderDescription,
				fromDate,
				toDate,
				filterType,
				orderTypeFilter,
				sellerIds,
				staffIds,
				selectedSettled,
				paginationDisabled
			);
			setTableData(responseData?.data);
			setTotal(responseData?.total);
			return responseData;
		} catch (err) {
			console.log(err);
		}
	};
	const onFilterOrders = (
		countryIds: any[],
		cityIds: any[],
		statusIds: any[],
		displayIds: any[],
		orderDescription: any,
		fromDate: any,
		toDate: any,
		filterType: any,
		orderTypeFilter: any,
		sellerIds: any[],
		staffIds: any[],
		selectedSettled: boolean | undefined
	) => {
		setFilterData({
			countryIds,
			cityIds,
			statusIds,
			displayIds,
			orderDescription,
			fromDate,
			toDate,
			filterType,
			orderTypeFilter,
			sellerIds,
			staffIds,
			selectedSettled,
		});
		setCurrentPage(1);
		getOrders(
			pageSize,
			1,
			countryIds,
			cityIds,
			statusIds,
			displayIds,
			orderDescription,
			fromDate,
			toDate,
			filterType,
			orderTypeFilter,
			sellerIds,
			staffIds,
			selectedSettled
		);
	};

	useEffect(() => {
		fetchTableData();
		setTableColumns([
			{
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 38,
			},
			{
				field: "displayId",
				headerName: "Kodi",
				valueFormatter: (params) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 100,
			},
			{
				field: "statusDescription",
				headerName: "Statusi",
				width: 150,
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
						</div>
					);
				},
			},
			{
				headerName: "Shitësi",
				cellRendererFramework: (params: any) => {
					const sellerUserName = params.data.sellerUserName;
					const sellerNumber = params.data.businessNr;
					const sellerUniqueId = params.data.uniqueSellerIdentifier;
					const sellerString: string =
						sellerUserName + " (" + sellerUniqueId + ") ";
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{sellerString}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{sellerNumber}
							</p>
						</div>
					);
				},
			},
			{
				headerName: "Blerësi",

				cellRendererFramework: (params: any) => {
					const buyerNameSurname = params.data.buyerNameAndSurname;
					const phone = params.data.buyerPhoneNo;
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{buyerNameSurname}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{phone}
							</p>
						</div>
					);
				},
			},
			{
				field: "orderToRedirectId",
				headerName: "OrderToRedirectId",
			},
			{ field: "packageDescription", headerName: "Emërtimi", width: 150 },
			{
				field: "country",
				headerName: "Shteti",
				width: 120,
			},
			{
				field: "city",
				headerName: "Qyteti",
				width: 120,
			},

			{
				field: "orderPrice",
				headerName: "Vlera e pakos",
				// Was a bare number with no currency indicator at all.
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				minWidth: 150,
				maxWidth: 150,
			},
			{
				field: "modal",
				headerName: " ",
				width: 250,
				cellRenderer: (params: any) => {
					return (
						<Button size="large" style={{ color: "green" }}>
							Hap
						</Button>
					);
				},
			},
		]);
	}, [setTableColumns]);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
			case "modal":
				await onModalOpen(params?.data);
				break;
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		// navigate("/order-details", { state: orderId });
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const onModalOpen = async (data: any) => {
		setOrderRedirectedId(data?.orderToRedirectId);
		await fetchOrderHistory(data?.orderToRedirectId);
		await fetchRedirectedNewOrder(data?.orderToRedirectId);
		setOpenModal(true);
	};
	const closeDetailsModal = () => {
		setOpenOrderHistory(false);
		setOpenModal(false);
	};

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const onPageChanged = (page: number, pageSize: number) => {
		setCurrentPage(page);
		getOrders(
			pageSize,
			page,
			filterData?.countryIds,
			filterData?.cityIds,
			filterData?.statusIds,
			filterData?.displayIds,
			filterData?.orderDescription,
			filterData?.fromDate,
			filterData?.toDate,
			filterData?.filterType,
			filterData?.orderTypeFilter,
			filterData?.sellerIds,
			filterData?.staffIds,
			filterData?.selectedSettled
		);
	};
	const handlePageSizeChange = async (current: number, size: number) => {
		setPageSize(size);
		await getOrders(
			size,
			current,
			filterData?.countryIds,
			filterData?.cityIds,
			filterData?.statusIds,
			filterData?.displayIds,
			filterData?.orderDescription,
			filterData?.fromDate,
			filterData?.toDate,
			filterData?.filterType,
			filterData?.orderTypeFilter,
			filterData?.sellerIds,
			filterData?.staffIds,
			filterData?.selectedSettled
		);
	};
	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex =
		startOrderIndex + Math.min(pageSize, total - startOrderIndex);

	const printTickets = async () => {
		if (!orderRedirectedId) {
			toast.warning("Kjo porosi nuk ekziston");
			return;
		}

		const params =
			selectedFormat === "A4"
				? { formatA4: true }
				: selectedFormat === "harleyTicket"
				? { harleyTicket: true }
				: {};
		const ids = [{ id: rowData?.displayId }];

		try {
			const response = await api.post("Order/print-order-ticket", ids, {
				params,
				responseType: "blob",
			});
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(
					"Popup eshte i bllokuar. Ju lutemi lejoni popup-ët për këtë faqe."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const printOrderList = async () => {
		const ids = grid?.current?.api.getSelectedRows()?.map((item: any) => {
			return { id: item.id };
		});

		if (!ids || ids.length === 0) {
			toast.warning("No orders selected for printing");
			return;
		}

		try {
			const response = await api.post("Order/order-list-details", ids, {
				responseType: "blob",
			});

			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(
					"Popup is blocked. Please allow pop-ups for this page."
				);
			}
		} catch (error) {
			console.error("Error exporting order list details:", error);
		}
	};

	const customOptions = [
		{
			label: "Printo listen",
			value: "print_list",
		},
	];

	return (
		<>
			<div className="orderMainContainer">
				<div className="ordersContainer">
					<FilterSection
						title="Porosite e ri-nisura"
						onFilterChange={onFilterOrders}
						onPrintList={printOrderList}
						selectedOptions={customOptions}
					/>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={total}
						startOrderIndex={startOrderIndex}
						endOrderIndex={endOrderIndex}
						onChange={onPageChanged}
						onShowSizeChange={handlePageSizeChange}
					/>
					<div
						className="ag-theme-alpine"
						style={{
							width: "100%",
							height: "auto",
						}}
					>
						<AgGridReact
							rowData={tableData}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							domLayout="autoHeight"
							onCellClicked={onCellClicked}
							suppressPaginationPanel={false}
							rowHeight={60}
						/>
					</div>
				</div>
			</div>
			<Modal
				open={openModal}
				onCancel={closeDetailsModal}
				title={"Detajet e porosise"}
				width={1200}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				footer={() => (
					<>
						<div className="flex items-end justify-end">
							<Button
								size="large"
								icon={<CloseOutlined />}
								onClick={() => {
									setOpenModal(false);
									setOrderRedirectedId(0);
								}}
							>
								Mbyll
							</Button>
							<Button
								size="large"
								style={{
									backgroundColor: "#5fa8d3",
									color: "black",
								}}
								icon={<PrinterOutlined />}
								type="primary"
								onClick={() => setIsPrintModalOpen(true)}
							>
								Printo tiketen
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="flex flex-row gap-10">
					<div className="flex flex-col rounded-lg border-2 border-slate-100 w-1/2">
						<OrderDetailRow
							label="Kodi"
							// value={registeredDate}
							value={rowData?.displayId}
							className="driverDetailRow"
							style={style.oddRow}
						/>
						<OrderDetailRow
							label="Data e regjistrimit"
							// value={registeredDate}
							value={rowData?.createdAt}
							className="driverDetailRow"
						/>
						<OrderDetailRow
							label="Statusi"
							value={rowData?.status}
							className="driverDetailRow"
							style={style.oddRow}
						/>
						<OrderDetailRow
							label="Bleresi"
							value={
								rowData?.buyerNameAndSurname +
								"\n" +
								rowData?.sectionDescription +
								", " +
								rowData?.buyerAddress +
								", " +
								rowData?.city +
								", " +
								rowData?.country +
								"\n" +
								rowData?.buyerPhoneNo
							}
							className="driverDetailRow"
						/>
						<OrderDetailRow
							label="Vlera e pakos"
							style={style.oddRow}
							value={formatMoney(
								rowData?.orderPrice,
								rowData?.orderCurrencyId
							)}
							className="driverDetailRow"
						/>
						<OrderDetailRow
							label="Shitesi"
							value={`${rowData?.sellerNameAndSurname}, ${rowData?.sellerCity},${rowData?.sellerCountry}, ${rowData?.sellerPhoneNo} `}
							className="driverDetailRow"
						/>
						<OrderDetailRow
							label="Mund te hapet"
							value={rowData?.openable?.toString()}
							style={style.oddRow}
						/>
						<OrderDetailRow
							label="E thyeshme"
							value={rowData?.fragile?.toString()}
						/>
						<OrderDetailRow
							label="Deklarohet"
							value={rowData?.declared?.toString()}
							style={style.oddRow}
						/>
					</div>
					<div>
						{openOrderHistory && (
							<Steps
								progressDot
								current={10}
								direction="vertical"
								items={orderHistory}
							/>
						)}
					</div>
				</div>
			</Modal>
			<Modal
				title="Zgjidh Formatin e Printimit"
				open={isPrintModalOpen}
				onOk={() => {
					setIsPrintModalOpen(false);
					printTickets();
				}}
				onCancel={() => setIsPrintModalOpen(false)}
				footer={
					<Buttons
						button1Text="Anulo"
						button2Text="Printo"
						onButton1Click={() => setIsPrintModalOpen(false)}
						onButton2Click={printTickets}
					/>
				}
			>
				<Radio.Group
					value={selectedFormat}
					onChange={(e) => setSelectedFormat(e.target.value)}
				>
					<Radio value="100x60">100x60</Radio>

					{hasAccessToA4 && <Radio value="A4">A4</Radio>}
					{hasAccessToHarleyTickets && (
						<Radio value="harleyTicket">Harley tiket</Radio>
					)}
				</Radio.Group>
			</Modal>
		</>
	);
};

export default RedirectedOrders;

const style = {
	oddRow: { backgroundColor: "#F9F8FC" },
};
